import Alert from '@amzn/meridian/alert';
import Box from '@amzn/meridian/box';
import DateRangePicker from '@amzn/meridian/date-range-picker';
import Heading from '@amzn/meridian/heading';
import Row from '@amzn/meridian/row';
import { parse, startOfDay } from 'date-fns';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { BorderedColumn, GreyBox, WaitFor } from '../components/blocks';
import { Text, useTranslation } from '../components/blocks/localization';
import { ClaimStatusIcon } from '../components/composites';
import { ClaimReferenceCell } from '../components/composites/ClaimReferenceCell';
import { PaginatedTable } from '../components/constructed';
import { CLAIMS_PER_PAGE, PDF_CLAIMS_MAX_RESULTS } from '../constants';
import { useViewClaimDocument } from '../hooks/useViewClaimDocument';
import {
    claimChangePage,
    claimUpdateDateRange,
    claimUpdateSortChange,
    getClaimList,
} from '../redux/actions/claimListActionCreators';
import { getResellerDetails } from '../redux/actions/resellerActionCreators';
import { ClaimMetadata, ClaimTableSortDirection, RootState, TableData } from '../redux/types';
import { IAsynchronouslyLoadedValue } from '../redux/types/asynchronouslyLoadedValue';
import { IAuthorizedReseller } from '../redux/types/reseller';
import { removeFileExtension } from '../utils/fileUtils';
import { sortClaimsByColumn } from '../utils/sortUtils';

type SortData = {
    sortColumn: string;
    sortDirection: string;
};

type FormattedDateRange = {
    fromDate: string;
    toDate: string;
};

const PDF_CLAIMS_HDSS_TENANT = 'PDF-CLAIMS';

// eslint-disable-next-line max-lines-per-function
const ClaimPdfListPage: FC<ClaimListPageProps> = ({
    reseller,
    resellerDetails,
    claimListState,
    getClaimList,
    getResellerDetails,
    claimChangePage,
    claimUpdateDateRange,
    claimUpdateSortChange,
}: ClaimListPageProps) => {
    const [currentReseller, setCurrentReseller] = useState<IAsynchronouslyLoadedValue<IAuthorizedReseller>>();
    const { t } = useTranslation('claimsListPage');
    const [localSort, setLocalSort] = useState<SortData | undefined>(undefined);
    const [fetchedDateRange, setFetchedDateRange] = useState<FormattedDateRange | undefined>(undefined);
    const [localFilteredClaims, setLocalFilteredClaims] = useState<ClaimMetadata[] | null>(null);
    const [localDateRange, setLocalDateRange] = useState<string[]>(claimListState.dateRange);
    const downloadDocument = useViewClaimDocument();

    useEffect(
        () => {
            if (
                claimListState.claimListData.status === 'Uninitialized' ||
                currentReseller?.value?.resellerId !== reseller.value?.resellerId
            ) {
                if (reseller.value) {
                    setCurrentReseller(reseller);
                    getClaimList(
                        reseller.value?.resellerId,
                        claimListState.currentPage,
                        PDF_CLAIMS_MAX_RESULTS,
                        claimListState.sortColumn,
                        claimListState.sortDirection,
                        claimListState.dateRange,
                        undefined,
                        true
                    );
                }
            }
            if (resellerDetails.status === 'Uninitialized' && reseller.value) {
                getResellerDetails(reseller.value.resellerId);
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [claimListState, reseller, resellerDetails]
    );

    useEffect(() => {
        if (claimListState.claimListData.status === 'Loaded') {
            const claims = claimListState.claimListData.value;
            if (!claims || claims.length === 0) return;

            const fromTime = claims[0].submissionDate;
            const toTime = claims[claims.length - 1].submissionDate;
            const isAscendingSort = claimListState.sortDirection === 'ascending';
            setFetchedDateRange({
                fromDate: formatDate(isAscendingSort ? fromTime : toTime),
                toDate: formatDate(isAscendingSort ? toTime : fromTime),
            });
        }
    }, [claimListState.claimListData, claimListState.sortDirection]);

    useEffect(() => {
        setLocalDateRange(claimListState.dateRange);
    }, [claimListState.dateRange]);

    const getAllData = async (): Promise<TableData<ClaimMetadata, 'resellerClaimNumber' | 'createdByUser'>> => {
        const claims = sortedClaimList || claimListState.claimListData.value || [];
        return getTableData(claims);
    };

    const handleViewClaimDocument = async (uploadId: string) => {
        if (reseller.value?.resellerId) {
            await downloadDocument(reseller.value.resellerId, uploadId, PDF_CLAIMS_HDSS_TENANT);
        }
    };

    const onSort = useCallback(
        (data: SortData) => {
            const { sortColumn, sortDirection } = data;
            const claimCount = (localFilteredClaims || claimListState?.claimListData?.value || []).length;
            if (sortColumn === 'creationDate' && claimCount >= PDF_CLAIMS_MAX_RESULTS && !localFilteredClaims) {
                setLocalSort(undefined);
                claimUpdateSortChange(
                    sortColumn,
                    ClaimTableSortDirection[sortDirection as keyof typeof ClaimTableSortDirection]
                );
            } else {
                setLocalSort({ sortColumn: sortColumn, sortDirection: sortDirection });
            }
        },
        [claimUpdateSortChange, claimListState.claimListData.value, localFilteredClaims]
    );

    // backend sorting done only for submission date. Local sorting used on the claims batch.
    // when local sorting submissionDate is used as a secondary sort criteria, always in descending order.
    const sortedClaimList = useMemo(() => {
        const claimsToSort = localFilteredClaims || claimListState.claimListData.value;
        if (!localSort || !claimsToSort) {
            return claimsToSort;
        }
        return sortClaimsByColumn(claimsToSort, localSort.sortColumn, localSort.sortDirection);
    }, [localSort, claimListState.claimListData.value, localFilteredClaims]);

    const createLocalDate = (dateString: string) => {
        return startOfDay(parse(dateString, 'yyyy-MM-dd', new Date()));
    };

    const isFilterRangeWithinBatch = (dateRange: string[]) => {
        if (!fetchedDateRange || !fetchedDateRange.toDate || !fetchedDateRange.fromDate) return false;
        const filterStartDate = createLocalDate(dateRange[0] || fetchedDateRange.fromDate);
        const filterEndDate = createLocalDate(dateRange[1] || fetchedDateRange.toDate);
        return (
            filterStartDate >= createLocalDate(fetchedDateRange.fromDate) &&
            filterEndDate <= createLocalDate(fetchedDateRange.toDate)
        );
    };

    const filterClaimsByDateRange = (claims: ClaimMetadata[], dateRange: string[]) => {
        const startDate = createLocalDate(dateRange[0] || fetchedDateRange!.fromDate);
        const endDate = createLocalDate(dateRange[1] || fetchedDateRange!.toDate);
        endDate.setHours(23, 59, 59, 999);

        return claims.filter((claim) => {
            const claimDate = new Date(claim.submissionDate * 1000);
            return claimDate >= startDate && claimDate <= endDate;
        });
    };

    function setDateRange(dateRange: string[]) {
        setLocalDateRange(dateRange);
        if (isFilterRangeWithinBatch(dateRange)) {
            const filteredClaims = filterClaimsByDateRange(claimListState.claimListData.value!, dateRange);
            setLocalFilteredClaims(filteredClaims);
            setLocalSort(undefined);
        } else {
            setLocalFilteredClaims(null);
            setLocalSort(undefined);
            claimUpdateDateRange(dateRange);
        }
    }

    const onPageChange = (value: string) => {
        const intValue = parseInt(value);
        if (intValue) {
            const calcNewStartRecord = CLAIMS_PER_PAGE * (intValue - 1);
            claimChangePage(intValue, calcNewStartRecord);
        }
    };

    const calculateNumberOfPages = (numOfclaim: number | undefined) => {
        if (numOfclaim) {
            return Math.ceil(numOfclaim / CLAIMS_PER_PAGE);
        }
        return 0;
    };

    const formatDate = (dateNumber: number) => {
        const date = new Date(dateNumber * 1000);
        return date.toISOString().split('T')[0];
    };

    const formatData = (claimList: ClaimMetadata[]) => {
        const claimsToUse = sortedClaimList || claimList;
        const startPos = (claimListState.currentPage - 1) * CLAIMS_PER_PAGE - claimListState.startAt;
        const endPos = claimListState.currentPage * CLAIMS_PER_PAGE - claimListState.startAt;

        const pageClaimList = claimsToUse.slice(startPos, endPos);

        return getTableData(pageClaimList);
    };

    const getCurrentSortState = () =>
        localSort || {
            sortColumn: claimListState.sortColumn,
            sortDirection: claimListState.sortDirection,
        };

    const getCurrentClaimsCount = (): number => {
        return localFilteredClaims?.length || claimListState.totalRecords || 0;
    };

    const getTableData = (claimList: ClaimMetadata[]) => {
        return new TableData(claimList, [
            {
                title: t('claimNumber-columnTitle'),
                uniqueId: 'resellerClaimNumber',
                format: 'resellerClaimNumber',
            },
            {
                title: t('file-columnTitle'),
                uniqueId: 'fileId',
                format: (claim: ClaimMetadata) => (
                    <ClaimReferenceCell
                        translatedText={t.cognate(removeFileExtension(claim.fileName || ''))}
                        fileId={claim.fileId}
                        callback={handleViewClaimDocument}
                    />
                ),
            },
            {
                title: t('createdByUser-columnTitle'),
                uniqueId: 'createdByUser',
                format: 'createdByUser',
            },
            {
                title: t('status-columnTitle'),
                uniqueId: 'status',
                format: (claim: ClaimMetadata) => (
                    <ClaimStatusIcon
                        status={claim.status}
                        errors={claim.errorList}
                        key={`${claim.fileId} - ClaimStatusIcon`}
                    />
                ),
            },
            {
                title: t('creationDate-columnTitle'),
                uniqueId: 'creationDate',
                format: (claim: ClaimMetadata) => (
                    <Text key={`${claim.fileId} - creationDateText`}>
                        {t('creationDate-columnValue', { dateTime: formatDate(claim.submissionDate) })}
                    </Text>
                ),
            },
        ]);
    };

    // only show the alert if we fetch the max number of claims
    const getLimitedClaimsFetchedAlert = () => {
        if (localFilteredClaims && localFilteredClaims.length < PDF_CLAIMS_MAX_RESULTS) return <></>;
        if ((claimListState?.claimListData?.value?.length ?? 0) >= PDF_CLAIMS_MAX_RESULTS) {
            return (
                <Alert size={'large'} type={'warning'} width={'100%'} id={'alert-limited-records-shown'}>
                    {t('alert-limited-records-shown', {
                        limit: PDF_CLAIMS_MAX_RESULTS,
                        fromDate: fetchedDateRange?.fromDate,
                        toDate: fetchedDateRange?.toDate,
                    })}
                </Alert>
            );
        }
        return <></>;
    };

    return (
        <BorderedColumn alignmentHorizontal={'center'} spacing={'none'}>
            <GreyBox type={'fill'} spacingInset={'medium'} width={'100%'}>
                <Heading level={5} alignment={'left'} data-testid={'claimListPage.header'}>
                    {t('claimList-pageHeading', { count: getCurrentClaimsCount() })}
                </Heading>
            </GreyBox>
            <WaitFor lazyValue={claimListState.claimListData}>
                {(claimListData) => (
                    <>
                        {getLimitedClaimsFetchedAlert()}
                        <PaginatedTable
                            data={formatData(claimListData)}
                            dataTestId={'claimListTable'}
                            currentPage={claimListState.currentPage}
                            numberOfPages={calculateNumberOfPages(getCurrentClaimsCount())}
                            onPageChange={onPageChange}
                            onSort={onSort}
                            sortColumn={getCurrentSortState().sortColumn}
                            sortDirection={getCurrentSortState().sortDirection}
                            key={`PaginatedTable`}
                            getAllData={getAllData}
                            dataName={t('claimList-pageHeading', { count: getCurrentClaimsCount() })}
                            rowKey={(rowArray: string[], rowObject: ClaimMetadata) => {
                                return rowObject.fileId;
                            }}
                        >
                            <Box
                                width={'90%'}
                                spacingInset={'100'}
                                data-testid={'claimListPage.filterCriteriaBox'}
                                key={'claimListPage.filterCriteriaBox'}
                            >
                                <Row spacing={'large'} key={'claimListPage.filterCriteriaBox - Row'}>
                                    <Row key={'claimListPage.filterCriteriaBox - Row'} className={'dateRangeContainer'}>
                                        <DateRangePicker
                                            value={localDateRange}
                                            onChange={setDateRange}
                                            monthsInView={2}
                                            clearButton={true}
                                            size={'medium'}
                                            allowSameStartAndEnd={true}
                                            startLabel={t('dateRangePicker-startPlaceholder')}
                                            endLabel={t('dateRangePicker-endPlaceHolder')}
                                            data-testid={'claimListPage.dateRangePicker'}
                                            key={'claimListPage.filterCriteriaBox - DateRangePicker'}
                                        />
                                    </Row>
                                </Row>
                            </Box>
                        </PaginatedTable>
                    </>
                )}
            </WaitFor>
        </BorderedColumn>
    );
};

const mapStateToProps = ({ resellerReducer, resellerClaimListReducer }: RootState) => {
    return {
        reseller: resellerReducer.selectedReseller,
        resellerDetails: resellerReducer.resellerDetails,
        claimListState: resellerClaimListReducer,
    };
};

const mapDispatchToProps = {
    getClaimList,
    claimChangePage,
    claimUpdateDateRange,
    claimUpdateSortChange,
    getResellerDetails,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
export type ClaimListPageProps = ConnectedProps<typeof connector>;
export default connector(ClaimPdfListPage);
